import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Users, Mail, Activity, TrendingUp, Calendar, CheckCircle, Clock, Plus, Copy, Check, MoreVertical, Ban, Trash2, Shield, ShieldOff, Loader2, AlertTriangle } from 'lucide-react';
import { getAdminStats, getAdminUsers, getInviteCodes, createInviteCode, suspendUser, deleteUser, updateUserRole, getAgentLogs, getAgentStats } from '@traction/api-client';
import type { AdminStats, AdminUser, InviteCode, AgentLog, AgentStats } from '@traction/api-client';
import { formatRelativeTime, classNames } from '@traction/shared';

function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to load admin stats:', error);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers ?? '--', icon: Users, color: 'text-cacao-gold' },
    { label: 'Active Users', value: stats?.activeUsers ?? '--', icon: CheckCircle, color: 'text-green-400' },
    { label: 'Total Leads', value: stats?.totalLeads ?? '--', icon: TrendingUp, color: 'text-blue-400' },
    { label: 'Leads This Week', value: stats?.leadsThisWeek ?? '--', icon: Calendar, color: 'text-purple-400' },
    { label: 'Emails Sent', value: stats?.emailsSent ?? '--', icon: Mail, color: 'text-orange-400' },
  ];

  return (
    <div className="p-6">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Admin Dashboard</h1>
      <p className="mt-0.5 text-sm text-cacao-muted">Platform overview and management.</p>

      <div className="mt-6 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        {statCards.map((stat) => (
          <div key={stat.label} className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-cacao-dark-tertiary">
                <stat.icon className={classNames('h-4 w-4', stat.color)} />
              </div>
              <div>
                <p className="text-xs text-cacao-muted">{stat.label}</p>
                <p className="text-lg font-semibold text-[#F5F5F5]">
                  {loading ? (
                    <span className="inline-block h-5 w-8 animate-pulse rounded bg-cacao-dark-tertiary" />
                  ) : (
                    stat.value
                  )}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="text-sm font-medium text-[#F5F5F5]">Quick Actions</h2>
        <div className="mt-3 flex gap-3">
          <a
            href="/admin/users"
            className="rounded-md border border-cacao-dark-tertiary px-4 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
          >
            View All Users
          </a>
          <a
            href="/admin/invites"
            className="rounded-md border border-cacao-dark-tertiary px-4 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
          >
            Manage Invite Codes
          </a>
        </div>
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const data = await getAdminUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSuspend(user: AdminUser) {
    setActionLoading(user.id);
    setMenuOpen(null);
    try {
      await suspendUser(user.id, !user.is_suspended);
      await loadUsers();
    } catch (error) {
      console.error('Failed to suspend user:', error);
      alert('Failed to update user status');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRoleChange(user: AdminUser) {
    setActionLoading(user.id);
    setMenuOpen(null);
    try {
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      await updateUserRole(user.id, newRole);
      await loadUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update user role');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(user: AdminUser) {
    setActionLoading(user.id);
    setDeleteConfirm(null);
    try {
      await deleteUser(user.id);
      await loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="p-6">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">User Management</h1>
      <p className="mt-0.5 text-sm text-cacao-muted">View and manage platform users.</p>

      <div className="mt-6 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="h-6 w-6 mx-auto animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-sm text-cacao-muted">No users found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-cacao-dark-tertiary bg-cacao-dark">
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Joined</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-cacao-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cacao-dark-tertiary">
              {users.map((user) => (
                <tr key={user.id} className={classNames(
                  'hover:bg-cacao-dark transition-colors',
                  user.is_suspended && 'opacity-60'
                )}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-[#F5F5F5]">
                      {user.first_name || user.full_name || 'Unknown'}
                      {user.last_name && ` ${user.last_name}`}
                    </p>
                    <p className="text-xs text-cacao-muted truncate max-w-[200px]">{user.id}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-cacao-muted">
                    {user.company_name || '--'}
                  </td>
                  <td className="px-4 py-3">
                    {user.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/20 px-2 py-0.5 text-xs font-medium text-purple-400">
                        <Shield className="h-3 w-3" />
                        Admin
                      </span>
                    ) : (
                      <span className="text-xs text-cacao-muted">User</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.is_suspended ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-400">
                        <Ban className="h-3 w-3" />
                        Suspended
                      </span>
                    ) : user.onboarding_completed_at ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-400">
                        <CheckCircle className="h-3 w-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-cacao-gold/20 px-2 py-0.5 text-xs font-medium text-cacao-gold">
                        <Clock className="h-3 w-3" />
                        Onboarding
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {formatRelativeTime(user.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {actionLoading === user.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-cacao-muted ml-auto" />
                    ) : (
                      <div className="relative inline-block">
                        <button
                          onClick={() => setMenuOpen(menuOpen === user.id ? null : user.id)}
                          className="p-1.5 rounded hover:bg-cacao-dark-tertiary transition-colors"
                        >
                          <MoreVertical className="h-4 w-4 text-cacao-muted" />
                        </button>
                        {menuOpen === user.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setMenuOpen(null)}
                            />
                            <div className="absolute right-0 top-8 z-20 w-40 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-secondary py-1 shadow-lg">
                              <button
                                onClick={() => handleRoleChange(user)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-cacao-muted hover:bg-cacao-dark-tertiary"
                              >
                                {user.role === 'admin' ? (
                                  <>
                                    <ShieldOff className="h-3.5 w-3.5" />
                                    Remove Admin
                                  </>
                                ) : (
                                  <>
                                    <Shield className="h-3.5 w-3.5" />
                                    Make Admin
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleSuspend(user)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-cacao-muted hover:bg-cacao-dark-tertiary"
                              >
                                <Ban className="h-3.5 w-3.5" />
                                {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                              </button>
                              <button
                                onClick={() => {
                                  setMenuOpen(null);
                                  setDeleteConfirm(user);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-400 hover:bg-cacao-dark-tertiary"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete User
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-cacao-text-tertiary">
        Showing {users.length} user{users.length !== 1 ? 's' : ''}
      </p>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-medium">Delete User</h3>
            </div>
            <p className="mt-3 text-sm text-cacao-muted">
              Are you sure you want to delete{' '}
              <span className="font-medium text-[#F5F5F5]">
                {deleteConfirm.first_name || deleteConfirm.full_name || 'this user'}
              </span>
              ? This will permanently delete all their data including leads, drafts, and settings.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-md border border-cacao-dark-tertiary px-4 py-2 text-xs font-medium text-cacao-muted hover:bg-cacao-dark-tertiary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="rounded-md bg-red-500 px-4 py-2 text-xs font-medium text-white hover:bg-red-600 transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminInvites() {
  const [invites, setInvites] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newMaxUses, setNewMaxUses] = useState(100);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadInvites();
  }, []);

  async function loadInvites() {
    try {
      const data = await getInviteCodes();
      setInvites(data);
    } catch (error) {
      console.error('Failed to load invite codes:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newCode.trim()) return;

    setCreating(true);
    try {
      await createInviteCode(newCode, newMaxUses);
      setNewCode('');
      setNewMaxUses(100);
      await loadInvites();
    } catch (error) {
      console.error('Failed to create invite code:', error);
      alert('Failed to create invite code');
    } finally {
      setCreating(false);
    }
  }

  function copyCode(code: string, id: string) {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <div className="p-6">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Invite Codes</h1>
      <p className="mt-0.5 text-sm text-cacao-muted">Manage beta access invite codes.</p>

      {/* Create new code */}
      <div className="mt-6 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
        <h2 className="text-sm font-medium text-[#F5F5F5]">Create New Code</h2>
        <div className="mt-3 flex gap-3">
          <input
            type="text"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="BETACODE"
            className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none uppercase"
          />
          <input
            type="number"
            value={newMaxUses}
            onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 100)}
            placeholder="Max uses"
            className="w-24 rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newCode.trim()}
            className="flex items-center gap-2 rounded-md bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        </div>
      </div>

      {/* Existing codes */}
      <div className="mt-6 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="h-6 w-6 mx-auto animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
          </div>
        ) : invites.length === 0 ? (
          <div className="p-6 text-center text-sm text-cacao-muted">No invite codes yet.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-cacao-dark-tertiary bg-cacao-dark">
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Usage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cacao-dark-tertiary">
              {invites.map((invite) => (
                <tr key={invite.id} className="hover:bg-cacao-dark transition-colors">
                  <td className="px-4 py-3">
                    <code className="rounded bg-cacao-dark px-2 py-1 text-sm font-mono text-cacao-gold">
                      {invite.code}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-sm text-cacao-muted">
                    {invite.current_uses} / {invite.max_uses}
                  </td>
                  <td className="px-4 py-3">
                    {invite.is_active ? (
                      <span className="inline-flex items-center rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-400">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-400">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {formatRelativeTime(invite.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => copyCode(invite.code, invite.id)}
                      className="p-1.5 rounded hover:bg-cacao-dark-tertiary transition-colors"
                      title="Copy code"
                    >
                      {copiedId === invite.id ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <Copy className="h-4 w-4 text-cacao-muted" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AdminAgents() {
  const [logs, setLogs] = useState<AgentLog[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<{ agentType?: 'scout' | 'scribe'; status?: 'completed' | 'failed' }>({});

  useEffect(() => {
    loadData();
  }, [filter]);

  async function loadData() {
    setLoading(true);
    try {
      const [logsData, statsData] = await Promise.all([
        getAgentLogs({ ...filter, limit: 100 }),
        getAgentStats(),
      ]);
      setLogs(logsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load agent data:', error);
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      case 'running': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-cacao-dark-tertiary text-cacao-muted';
    }
  };

  const getAgentColor = (agentType: string) => {
    return agentType === 'scout' ? 'text-purple-400' : 'text-blue-400';
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="p-6">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Agent Monitoring</h1>
      <p className="mt-0.5 text-sm text-cacao-muted">Monitor Scout and Scribe agent activity.</p>

      {/* Stats Cards */}
      {stats && (
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <p className="text-xs text-cacao-muted">Total Runs</p>
            <p className="text-lg font-semibold text-[#F5F5F5]">{stats.totalRuns}</p>
          </div>
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <p className="text-xs text-cacao-muted">Success Rate</p>
            <p className="text-lg font-semibold text-green-400">
              {stats.totalRuns > 0 ? Math.round((stats.successfulRuns / stats.totalRuns) * 100) : 0}%
            </p>
          </div>
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <p className="text-xs text-cacao-muted">Total Tokens</p>
            <p className="text-lg font-semibold text-[#F5F5F5]">{stats.totalTokens.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <p className="text-xs text-cacao-muted">Avg Duration</p>
            <p className="text-lg font-semibold text-[#F5F5F5]">{stats.avgDurationMs}ms</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex gap-3">
        <select
          value={filter.agentType || ''}
          onChange={(e) => setFilter({ ...filter, agentType: e.target.value as 'scout' | 'scribe' | undefined || undefined })}
          className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-xs text-[#F5F5F5] focus:border-cacao-gold focus:outline-none"
        >
          <option value="">All Agents</option>
          <option value="scout">Scout</option>
          <option value="scribe">Scribe</option>
        </select>
        <select
          value={filter.status || ''}
          onChange={(e) => setFilter({ ...filter, status: e.target.value as 'completed' | 'failed' | undefined || undefined })}
          className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-xs text-[#F5F5F5] focus:border-cacao-gold focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Logs Table */}
      <div className="mt-4 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="h-6 w-6 mx-auto animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-6 text-center text-sm text-cacao-muted">
            <Activity className="h-8 w-8 mx-auto mb-2 text-cacao-dark-tertiary" />
            No agent activity recorded yet.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-cacao-dark-tertiary bg-cacao-dark">
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Tokens</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-cacao-muted">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cacao-dark-tertiary">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-cacao-dark transition-colors">
                  <td className="px-4 py-3">
                    <span className={classNames('text-xs font-medium uppercase', getAgentColor(log.agent_type))}>
                      {log.agent_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-cacao-muted">
                    {formatAction(log.action)}
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {log.user_name || 'Unknown'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={classNames(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                      getStatusColor(log.status)
                    )}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {log.tokens_used?.toLocaleString() || '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-cacao-muted">
                    {formatRelativeTime(log.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-cacao-text-tertiary">
        Showing {logs.length} log{logs.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
}

export function AdminDashboardPage() {
  return (
    <Routes>
      <Route index element={<AdminOverview />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="invites" element={<AdminInvites />} />
      <Route path="agents" element={<AdminAgents />} />
    </Routes>
  );
}
